import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Typography } from '@neo/primitives/typography'
import type { ReactNode } from 'react'
import type { CallRecordingFormValues } from 'v2/api-console/voice/voice-settings/types'
import { z } from 'zod'

export const CALL_RECORDING_HELP = {
  inbound: 'Also applies to inbound calls that AI agents and Stream nodes handle in your flows.',
  outbound:
    'Also applies to outbound calls that AI agents and Stream nodes handle, and to calls that your flows forward with Call Forward.',
  scope:
    'These settings apply to your whole organization, including sub accounts. If a node in a flow has its own Record setting, that setting is used instead.',
}

// Lines up with the label of the switch above it and pulls up into the form's row gap.
// Slots skip the field wrapper's col-span-full, so they carry it for grid forms.
const SwitchHelpText = ({ children }: { children: ReactNode }) => (
  <Typography variant="P/400" className="col-span-full -mt-3 pl-9 text-secondary">
    {children}
  </Typography>
)

// One definition for the Voice Call page and the Voice settings tab, which edit the same two switches.
export const getCallRecordingFields = (
  canEdit = true,
): GenericFieldConfig<CallRecordingFormValues> => [
  {
    name: 'channel_settings.call_recording_inbound',
    label: 'Record all incoming calls',
    componentType: 'switch',
    zodValidation: z.boolean(),
    disabled: () => !canEdit,
    inputProps: { 'data-testid': 'switch-call-recording-inbound' },
  },
  {
    name: 'call_recording_inbound_help',
    type: 'slot',
    component: <SwitchHelpText>{CALL_RECORDING_HELP.inbound}</SwitchHelpText>,
  },
  {
    name: 'channel_settings.call_recording_outbound',
    label: 'Record all outgoing calls',
    componentType: 'switch',
    zodValidation: z.boolean(),
    disabled: () => !canEdit,
    inputProps: { 'data-testid': 'switch-call-recording-outbound' },
  },
  {
    name: 'call_recording_outbound_help',
    type: 'slot',
    component: <SwitchHelpText>{CALL_RECORDING_HELP.outbound}</SwitchHelpText>,
  },
  {
    name: 'call_recording_scope_note',
    type: 'slot',
    component: (
      <Typography variant="P/400" className="col-span-full text-secondary">
        {CALL_RECORDING_HELP.scope}
      </Typography>
    ),
  },
]
